import { api } from './index';

export interface InviteCompanyAdminsRequest {
  emails: string[];
  roleId?: string;
}

export interface InvitedAdmin {
  email: string;
  workosInvitationId: string;
  adminId: string;
}

export interface InviteCompanyAdminsResponse {
  invitedAdmins: InvitedAdmin[];
  totalInvited: number;
  companyId: string;
}

export interface Role {
  id: string;
  name: string;
  level: number;
  description?: string;
}

export interface RolePermission {
  id: string;
  name: string;
  description?: string;
}

export interface RoleWithPermissions extends Role {
  permissions?: RolePermission[];
}

export interface GetInvitableRolesResponse {
  data: Role[];
}

export interface UpdateMemberRoleRequest {
  roleName: string;
}

export interface UpdateMemberRoleResponse {
  userId: string;
  companyId: string;
  roleId: string;
  roleName: string;
  workosSynced: boolean;
  success: boolean;
}

export interface UpdateMemberProfileRequest {
  first_name?: string;
  last_name?: string;
  title?: string;
  department?: string;
  phone?: string;
}

export interface UpdateMemberProfileResponse {
  userId: string;
  companyId: string;
  success: boolean;
}

export interface RemoveMemberResponse {
  membershipId: string;
  success: boolean;
  message: string;
}

export interface SetMemberActiveResponse {
  id: string;
  user_id: string;
  company_id: string;
  active: boolean;
}

export const adminApi = {
  /**
   * Get roles that can be invited as company admins
   */
  getInvitableRoles: async (): Promise<GetInvitableRolesResponse> => {
    const response = await api.get('/roles/admin/invitable-company-admins');
    return response.data;
  },

  /**
   * Get a single role by name, including its permissions.
   * GET /roles/admin/:roleName returns the role aggregate with `permissions`.
   */
  getRoleWithPermissions: async (
    roleName: string
  ): Promise<RoleWithPermissions> => {
    const response = await api.get(`/roles/admin/${roleName}`);
    return response.data;
  },

  /**
   * Invite admins to current user's company
   */
  inviteCompanyAdmins: async (
    data: InviteCompanyAdminsRequest
  ): Promise<InviteCompanyAdminsResponse> => {
    const response = await api.post(
      '/companies/admin/me/invite-admins',
      data
    );
    return response.data;
  },

  /**
   * Update a company member's role
   */
  updateMemberRole: async (
    companyId: string,
    userId: string,
    data: UpdateMemberRoleRequest
  ): Promise<UpdateMemberRoleResponse> => {
    const response = await api.put(
      `/companies/admin/${companyId}/members/${userId}/role`,
      data
    );
    return response.data;
  },

  /**
   * Update a company member's profile fields (name, title, dept, phone)
   */
  updateMemberProfile: async (
    companyId: string,
    userId: string,
    data: UpdateMemberProfileRequest
  ): Promise<UpdateMemberProfileResponse> => {
    const response = await api.patch(
      `/companies/admin/${companyId}/members/${userId}/profile`,
      data
    );
    return response.data;
  },

  /**
   * Remove a member from company
   */
  removeMember: async (
    userId: string
  ): Promise<RemoveMemberResponse> => {
    const response = await api.delete(
      `/companies/admin/members/${userId}`
    );
    return response.data;
  },

  /**
   * Archive (active=false) or reactivate (active=true) a company member.
   * Soft + reversible — distinct from removeMember (hard delete).
   */
  setMemberActive: async (
    companyId: string,
    userId: string,
    active: boolean
  ): Promise<SetMemberActiveResponse> => {
    const response = await api.put(
      `/companies/admin/${companyId}/members/${userId}/active`,
      { active }
    );
    return response.data;
  },
};
